import { useRef, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";

interface TalentSearchBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  disciplines: string[];
  discipline: string;
  onDisciplineChange: (v: string) => void;
  filterCount: number;
  onOpenFilters: () => void;
}

export function TalentSearchBar({
  search,
  onSearchChange,
  disciplines,
  discipline,
  onDisciplineChange,
  filterCount,
  onOpenFilters,
}: TalentSearchBarProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions =
    focused && search.trim().length > 0
      ? disciplines.filter(
          (d) =>
            d.toLowerCase().includes(search.toLowerCase().trim()) &&
            discipline !== d,
        )
      : [];

  const selectSuggestion = (d: string) => {
    onDisciplineChange(d);
    onSearchChange("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none z-10" />
          <Input
            ref={inputRef}
            placeholder="Search name or title..."
            className="pl-9 bg-zinc-900/50 border-white/10 focus-visible:ring-primary/50 text-white font-sans"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          {suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-zinc-900 border border-white/10 rounded-md shadow-xl overflow-hidden">
              {suggestions.slice(0, 7).map((s) => (
                <button
                  key={s}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectSuggestion(s);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm font-sans text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onOpenFilters}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-semibold font-sans transition-colors shrink-0 ${
            filterCount > 0
              ? "bg-primary/20 border-primary/40 text-primary"
              : "bg-zinc-900/50 border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
          }`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {filterCount > 0 ? `Filters (${filterCount})` : "Filters"}
        </button>
      </div>

      {discipline !== "all" && (
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold font-sans bg-primary/20 border border-primary/40 text-primary">
            {discipline}
            <button
              type="button"
              onClick={() => onDisciplineChange("all")}
              className="text-primary/60 hover:text-primary transition-colors leading-none"
              aria-label={`Remove ${discipline} filter`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
